import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import { makeStyles } from '@material-ui/styles'
import { SnackbarProvider } from 'notistack'
import React, { FC, useCallback, useMemo, useState } from 'react'

import { ToastContext, ToastId } from '../lib/toasts/context'

const useStyles = makeStyles(() => ({
  containerRoot: {
    zIndex: 999999999,
    '@media(min-width: 600px)': {
      maxWidth: '560px',
    },
  },
  variantSuccess: {
    backgroundColor: '#C7E3D0',
    border: '1px solid #5AAD75',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'Gotham',
    fontWeight: 400,
    color: '#000000',
  },
  variantError: {
    backgroundColor: '#F5BABA',
    border: '1px solid #E64B37',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'Gotham',
    fontWeight: 400,
    color: '#000000',
  },
  variantWarning: {
    backgroundColor: '#FFF7D3',
    border: '1px solid #FFD000',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'Gotham',
    fontWeight: 400,
    color: '#000000',
  },
}))

const CustomSuccessIcon = () => (
  <CheckCircleIcon className="h-6 w-6 mr-2.5 rounded-full" style={{ fill: '#4A9462' }} />
)

const CustomErrorIcon = () => (
  <CancelIcon className="h-6 w-6 mr-2.5 rounded-full" style={{ fill: '#E64B37' }} />
)

const CustomWarningIcon = () => (
  <WarningIcon className="h-6 w-6 mr-2.5 rounded-full" style={{ fill: '#FFD000' }} />
)

const ToastContextProvider: FC = ({ children }) => {
  const [toasts, setToasts] = useState<ToastId[]>(() => [])
  const [dismissed, setDismissed] = useState<ToastId[]>(() => [])

  const isToastShown = useCallback((toastId: ToastId) => toasts.includes(toastId), [toasts])

  const isToastDismissed = useCallback(
    (toastId: ToastId) => dismissed.includes(toastId),
    [dismissed],
  )

  // use single hook to memoize actions without deps
  const actions = useMemo(
    () => ({
      showToast: (toastId: ToastId) => {
        setToasts((currentToasts) => {
          if (currentToasts.includes(toastId)) {
            return currentToasts
          }
          return [...currentToasts, toastId]
        })
      },

      hideToast: (toastId: ToastId) => {
        setToasts((currentToasts) => {
          if (!currentToasts.includes(toastId)) {
            return currentToasts
          }
          const newToasts = [...currentToasts]
          newToasts.splice(newToasts.indexOf(toastId), 1)
          return newToasts
        })
      },

      dismissToast: (toastId: ToastId) => {
        setDismissed((currentDismissed) => {
          if (currentDismissed.includes(toastId)) {
            return currentDismissed
          }
          return [...currentDismissed, toastId]
        })
      },
    }),
    [],
  )

  const classes = useStyles()

  return (
    <ToastContext.Provider
      value={{
        ...actions,
        toasts,
        dismissed,
        isToastShown,
        isToastDismissed,
      }}
    >
      <SnackbarProvider
        maxSnack={4}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        classes={{
          containerRoot: classes.containerRoot,
          variantSuccess: classes.variantSuccess,
          variantError: classes.variantError,
          variantWarning: classes.variantWarning,
        }}
        iconVariant={{
          success: <CustomSuccessIcon />,
          error: <CustomErrorIcon />,
          warning: <CustomWarningIcon />,
        }}
      >
        {children}
      </SnackbarProvider>
    </ToastContext.Provider>
  )
}

export default ToastContextProvider
